var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import useSWR from "swr";
import { axiosPrivate } from "../../api/axios";
import { apFetcher } from "../Utilities/axiosPrivateFetcher_SWR";
import { PUBLIC_SWR_KEYS } from "../Utilities/swrKeys";
/**
 * Hydrates public data that doesn't require authentication.
 * This component should be placed at the top level, outside of any authentication context.
 *
 * Data hydrated here:
 * - Company provider settings
 * - Delivery address questions
 * - Company address
 * - Plans (program configurations)
 * - General settings (top bar, redirect URL, header content)
 * - Account settings (hide/show nav sections, feature toggles)
 * - Register form data (referrals, privacy policy)
 *
 * See HYDRATION_README.md for details.
 * @returns null
 */
export const PublicDataHydrator = () => {
    // Hydrate company provider data on load to ensure it's available globally.
    useSWR(PUBLIC_SWR_KEYS.COMPANY_PROVIDER, apFetcher(`/React/GetCompanyProviderModel`), {
        revalidateOnMount: true,
    });
    // Hydrate delivery questions data on load to ensure it's available globally.
    useSWR(PUBLIC_SWR_KEYS.DELIVERY_QUESTIONS, apFetcher(`/Account/GetAccountDeliveryAddressQuestions`), {
        revalidateOnMount: true,
    });
    // Hydrate company address data on load to ensure it's available globally.
    useSWR(PUBLIC_SWR_KEYS.COMPANY_ADDRESS, apFetcher(`/Account/GetCompanyAddress`), {
        revalidateOnMount: true,
    });
    // Hydrate plans data on load to enable instant client-side lookups of plan properties.
    // Plans contain program configurations like usesLocation, usesZipCodes, usesChooseMeals, etc.
    // This is company-wide data that doesn't require authentication.
    useSWR(PUBLIC_SWR_KEYS.PLANS, apFetcher(`/React/GetPlans`), {
        revalidateOnMount: true,
    });
    // Hydrate general settings on load for layout decisions (top bar, redirect URL, header content).
    // Uses POST (not GET), so apFetcher can't be used — inline fetcher instead.
    // The sku parameter is always undefined at the root level (no :sku in the route path).
    useSWR(PUBLIC_SWR_KEYS.GENERAL_SETTINGS, () => __awaiter(void 0, void 0, void 0, function* () {
        const res = yield axiosPrivate.post(`/React/GetGeneralConfigurations?sku=undefined`);
        return res.data;
    }), {
        revalidateOnMount: true,
    });
    // Hydrate account-level display settings on load (hide/show nav sections, feature toggles).
    // This is company-wide configuration, not user-specific.
    useSWR(PUBLIC_SWR_KEYS.ACCOUNT_SETTINGS, apFetcher(`/Account/AccountSettings`), {
        revalidateOnMount: true,
    });
    // Hydrate register form data on load (referrals, privacy policy).
    // Only consumed by the register page, but hydrated here to avoid per-navigation fetches.
    useSWR(PUBLIC_SWR_KEYS.REGISTER_FORM_DATA, apFetcher(`/GetRegisterFormData`), {
        revalidateOnMount: true,
    });
    return null;
};
